import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { login, signup, recoverPassword } from '../api';

interface Props {
  open: boolean;
  onClose: () => void;
  onLoggedIn: () => void;
  message?: string | null;
}

type Mode = 'login' | 'signup' | 'forgot';

export function LoginModal({ open, onClose, onLoggedIn, message }: Props) {
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setUsername('');
      setPassword('');
      setRecoveryKey('');
      setError(null);
      setSuccess(null);
      setMode('login');
    }
  }, [open]);

  const handleSignup = async () => {
    if (!name.trim()) {
      setError('Enter a name for the family head.');
      return;
    }
    if (!username.trim() || !password) {
      setError('Enter both username and password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await signup(name.trim(), username.trim(), password);
      if (result.recoveryKey) {
        setRecoveryKey(result.recoveryKey);
        setMode('login');
      } else {
        onClose();
        onLoggedIn();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed';
      setError(msg);
      if (/already exists/i.test(msg)) setMode('login');
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async () => {
    if (!username.trim() || !password) {
      setError('Enter both username and password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
      onLoggedIn();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleRecover = async () => {
    if (!username.trim() || !password || !recoveryKey) {
      setError('Enter username, recovery key, and new password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await recoverPassword(username.trim(), recoveryKey.trim(), password);
      setSuccess('Password reset successful! You can now log in with your new password.');
      setRecoveryKey('');
      setPassword('');
      setMode('login');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const save = mode === 'signup' ? handleSignup : mode === 'forgot' ? handleRecover : handleLogin;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-slate-950/70 p-0 sm:items-center sm:p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'login' ? 'Login' : mode === 'signup' ? 'Create family account' : 'Reset password'}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-100">
            {mode === 'login' ? '🔑 Login' : mode === 'signup' ? '🏠 Create family account' : '🔒 Reset password'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-slate-400 transition hover:bg-slate-800 hover:text-slate-200"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {message && (
          <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            ⚠️ {message}
          </p>
        )}

        {/* Recovery key display — shown once after signup */}
        {recoveryKey && (
          <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
            <p className="mb-2 text-sm font-semibold text-emerald-300">
              ✅ Account created! Save this recovery key somewhere safe:
            </p>
            <code className="block rounded-lg bg-slate-800 px-3 py-2 text-center text-lg font-mono font-bold tracking-wider text-emerald-400">
              {recoveryKey}
            </code>
            <p className="mt-2 text-xs text-emerald-400/80">
              You will need this to reset your password if you forget it. It will NOT be shown again.
            </p>
            <button
              type="button"
              onClick={() => setRecoveryKey('')}
              className="mt-3 w-full rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500"
            >
              I have saved my recovery key
            </button>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
            <p className="text-sm text-emerald-300">{success}</p>
            <button
              type="button"
              onClick={() => { setSuccess(null); setMode('login'); }}
              className="mt-3 w-full rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500"
            >
              Go to login
            </button>
          </div>
        )}

        {/* Main form — hide when showing recovery key or success */}
        {!recoveryKey && !success && (
          <>
            {mode !== 'forgot' && (
              <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl bg-slate-800 p-1 text-sm font-semibold">
                {(['login', 'signup'] as Mode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setMode(m); setError(null); }}
                    className={`rounded-lg px-3 py-1.5 transition ${
                      mode === m ? 'bg-zinc-700 text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {m === 'login' ? 'Log in' : 'Create family account'}
                  </button>
                ))}
              </div>
            )}

            {mode === 'signup' && (
              <p className="mb-4 text-sm text-slate-400">
                First-time setup: creates the <span className="text-slate-200">family head</span> account.
                After that, add members via 👥 Family.
              </p>
            )}

            {mode === 'signup' && (
              <>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Your name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Alex"
                  className="mb-4 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 outline-none transition focus:border-zinc-500"
                />
              </>
            )}

            {mode === 'forgot' && (
              <p className="mb-4 text-sm text-slate-400">
                Enter your username, recovery key, and a new password to regain access.
              </p>
            )}

            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && save()}
              placeholder={mode === 'signup' ? 'e.g. alex' : 'your username'}
              autoFocus
              className="mb-4 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 outline-none transition focus:border-zinc-500"
            />

            {mode !== 'forgot' && (
              <>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && save()}
                  placeholder={mode === 'signup' ? 'at least 6 characters' : '••••••••'}
                  className="mb-4 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 outline-none transition focus:border-zinc-500"
                />
              </>
            )}

            {mode === 'forgot' && (
              <>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Recovery key
                </label>
                <input
                  type="text"
                  value={recoveryKey}
                  onChange={(e) => setRecoveryKey(e.target.value)}
                  placeholder="ABCD-1234-EFGH-5678"
                  className="mb-4 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 outline-none transition focus:border-zinc-500"
                />
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  New password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRecover()}
                  placeholder="at least 6 characters"
                  className="mb-4 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 outline-none transition focus:border-zinc-500"
                />
              </>
            )}

            {error && <p className="mb-3 text-sm text-rose-400">⚠️ {error}</p>}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-300 transition hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="rounded-xl bg-zinc-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-zinc-600 disabled:opacity-50"
              >
                {busy ? 'Please wait…' : mode === 'login' ? 'Log in' : mode === 'signup' ? 'Create account' : 'Reset password'}
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-slate-600">
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(null); }}
                  className="text-slate-500 hover:text-slate-300"
                >
                  Forgot password?
                </button>
              )}
              {mode === 'login' && <span> · </span>}
              {mode === 'login' ? 'New family? Switch to “Create family account”.' : 'Already set up? '}
              {mode !== 'login' && (
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(null); }}
                  className="text-slate-500 hover:text-slate-300"
                >
                  Switch to Log in
                </button>
              )}
            </p>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
